import {noop} from '@github-ui/noop'
import {createTanStackRouter, Outlet, type RouteObject, type TanStackRouterHistory} from '@github-ui/react-router'
import type {ComponentType, ReactNode} from 'react'

import {BaseProviders} from '../BaseProviders'
import {CommonElements} from '../CommonElements'
import {RouterDevTools} from '../RouterDevTools'
import {RoutesContextProvider} from '../RoutesContextProvider'
import {AppPayloadContext} from '../use-app-payload'
import {NavigationFocusListener} from './NavigationFocusListener'
import {PublishPayload} from './PublishPayload'
import {
  RootAppRouteErrorElement,
  RootAppRouteErrorInner,
  UnhandledRouteError,
  UnhandledRouteErrorInner,
} from './RouterErrorBoundary'
import {CombinedScrollRestoration} from './ScrollRestoration'
import {SoftNavLifecycleListener} from './SoftNavLifecycleListener'
import {TitleManager} from './TitleManager'

export function routesWithProviders(
  routes: RouteObject[],
  options: Omit<SharedProviderOptions, 'tanstackRouterEnabled'> & {
    HydrateFallback?: ComponentType
  },
): RouteObject[] {
  const {HydrateFallback, ...rest} = options
  return [
    {
      id: `__DATA_ROUTER_ROOT__`,
      errorElement: <UnhandledRouteError appName={rest.appName} />,
      HydrateFallback,
      element: <SharedProviders routes={routes} options={rest} />,
      children: [
        {
          id: `__DATA_ROUTER_APPLICATION_ROUTES__`,
          errorElement: <RootAppRouteErrorElement appName={rest.appName} />,
          children: routes,
        },
      ],
    },
  ]
}

export function tanStackRouterWithProviders<TContext extends {}>(
  routes: RouteObject[],
  options: Omit<SharedProviderOptions, 'tanstackRouterEnabled' | 'dataRouterEnabled'> & {
    context: TContext
    history?: TanStackRouterHistory
    HydrateFallback?: ComponentType
  },
) {
  const {history, context, HydrateFallback, ...rest} = options
  const rootOptions = {...rest, dataRouterEnabled: true, tanstackRouterEnabled: true}

  const RootComponent = () => <SharedProviders routes={routes} options={rootOptions} />

  const RootErrorComponent = ({error}: {error: unknown}) => (
    <UnhandledRouteErrorInner routeError={error} appName={rest.appName} />
  )

  const AppRoutesErrorComponent = ({error}: {error: unknown}) => (
    <RootAppRouteErrorInner routeError={error} appName={rest.appName} />
  )

  return createTanStackRouter({
    context,
    rootComponent: RootComponent,
    rootPendingComponent: HydrateFallback ? () => <HydrateFallback /> : undefined,
    rootErrorComponent: RootErrorComponent,
    appErrorComponent: AppRoutesErrorComponent,
    routeObjects: routes,
    history,
    // Errors are already reported by error components.
    // Providing a defaultOnCatch suppresses duplicate reporting in createOnCaughtError.
    defaultOnCatch: noop,
  })
}

type SharedProviderOptions = {
  appPayload?: Record<string, unknown>
  appName: string
  ssrError: HTMLScriptElement | undefined
  dataRouterEnabled: boolean
  tanstackRouterEnabled?: boolean
  children?: ReactNode
}

/**
 * Shared provider tree used by both `routesWithProviders` (React Router) and
 * `tanStackRouterWithProviders` (TanStack Router).
 *
 * When `tanstackRouterEnabled` is true, `RoutesContextProvider` is omitted
 * (TSR manages its own route context) and hooks that lack TSR shims are suppressed.
 */
function SharedProviders({
  routes,
  options: {appPayload, appName, ssrError, children, dataRouterEnabled, tanstackRouterEnabled},
}: {
  routes: RouteObject[]
  options: SharedProviderOptions
}) {
  const sharedInternals = (
    <>
      <Outlet />
      {children}
      <CommonElements ssrError={ssrError} />
      <SoftNavLifecycleListener />
      {/* NavigationFocusListener uses useNavigationType(), which has no TSR shim yet */}
      {tanstackRouterEnabled ? null : <NavigationFocusListener />}
      {/* CombinedScrollRestoration uses React Router's <ScrollRestoration>, which has no TSR equivalent yet */}
      {tanstackRouterEnabled ? null : <CombinedScrollRestoration />}
      <PublishPayload />
      <TitleManager />
      <RouterDevTools routes={routes} />
    </>
  )
  return (
    <BaseProviders appName={appName} dataRouterEnabled={dataRouterEnabled}>
      <AppPayloadContext value={appPayload}>
        {tanstackRouterEnabled ? (
          sharedInternals
        ) : (
          <RoutesContextProvider routes={routes}>{sharedInternals}</RoutesContextProvider>
        )}
      </AppPayloadContext>
    </BaseProviders>
  )
}
